'use client';

import { useState, useEffect } from 'react';
import { useRegionalFormatters } from '@/lib/use-regional-formatters';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DollarSign } from 'lucide-react';

interface Currency {
  code: string;
  name: string;
  symbol: string;
}

export function CurrencySelector() {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currentCurrency, setCurrentCurrency] = useState('USD');

  useEffect(() => {
    const savedCurrency = document.cookie
      .split('; ')
      .find((row) => row.startsWith('NEXT_CURRENCY='))
      ?.split('=')[1];

    if (savedCurrency) {
      setCurrentCurrency(savedCurrency);
    }

    fetch('/api/cms/currencies')
      .then((res) => res.json())
      .then((data) => setCurrencies(data.currencies || []))
      .catch(() => setCurrencies([{ code: 'USD', name: 'US Dollar', symbol: '$' }]));
  }, []);

  const handleCurrencyChange = (newCurrency: string) => {
    setCurrentCurrency(newCurrency);
    document.cookie = `NEXT_CURRENCY=${newCurrency}; path=/; max-age=31536000; samesite=lax`;
    window.dispatchEvent(new CustomEvent('currencyChange', { detail: newCurrency }));
  };

  const current = currencies.find((curr) => curr.code === currentCurrency);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <DollarSign className="h-4 w-4" />
          <span className="hidden sm:inline">{current?.code || currentCurrency}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {currencies.map((curr) => (
          <DropdownMenuItem
            key={curr.code}
            onClick={() => handleCurrencyChange(curr.code)}
            className={curr.code === currentCurrency ? 'bg-accent' : ''}
          >
            {curr.symbol} {curr.code} - {curr.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
